import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import Logo from "./Logo";

/* ──────────────────────────────────────────────────────────────────
 *  EASTER EGGS  —  bundled provider
 *
 *  Installed globally via <EasterEggsProvider> in App.js.
 *  Provides:
 *    1. Konami code (↑↑↓↓←→←→BA) → confetti + unlock /secret + toast
 *    2. Typing  "ulties" anywhere → 3D logo spin overlay for ~2s
 *    3. Click site logo 7× in 3s → friendly coffee invite toast
 *    4. ?  key → keyboard shortcuts cheatsheet overlay
 *    5. g h / g w / g b / g c → vim-style nav (home / work / blog / contact)
 *    6. Stylish console banner on every page load
 *    7. 3am detection → silent console wink
 *  ──────────────────────────────────────────────────────────────── */

const EasterEggsContext = createContext({ logoClick: () => {} });
export const useEasterEggs = () => useContext(EasterEggsContext);

const KONAMI = [
  "ArrowUp", "ArrowUp",
  "ArrowDown", "ArrowDown",
  "ArrowLeft", "ArrowRight",
  "ArrowLeft", "ArrowRight",
  "b", "a",
];

const UNLOCK_KEY = "ulties-secret-unlocked";

function ConsoleBanner() {
  useEffect(() => {
    const style1 = "font: 600 18px/1.4 system-ui; color:#fafafa; background:#050505; padding:6px 10px; border-radius:6px;";
    const style2 = "color:#a1a1aa; font: 13px/1.5 system-ui;";
    console.log("%cUlties · tech solutions", style1);
    console.log(
      "%cPoking around the source? Good.\n" +
      "We're hiring senior engineers — careers@ulties.com\n" +
      "P.S. there are five hidden interactions on this site.",
      style2,
    );
    if (new Date().getHours() === 3) {
      console.log("%c👀 Still up at 3am? Same.", "color:#34d399;font:600 14px system-ui");
    }
  }, []);
  return null;
}

/* ── 1. Konami confetti ─────────────────────────────────────────── */
function KonamiConfetti({ show, onDone }) {
  if (!show) return null;
  const pieces = Array.from({ length: 60 });
  return (
    <div
      className="fixed inset-0 z-[90] pointer-events-none"
      data-testid="konami-confetti"
      onAnimationEnd={onDone}
    >
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.2;
        const duration = 2 + Math.random() * 1.5;
        const rotate = Math.random() * 720 - 360;
        const colors = ["#fafafa", "#34d399", "#a78bfa", "#22d3ee", "#ec4899"];
        const bg = colors[i % colors.length];
        return (
          <motion.span
            key={i}
            initial={{ y: -40, x: 0, rotate: 0, opacity: 1 }}
            animate={{ y: window.innerHeight + 80, rotate, opacity: 0 }}
            transition={{ duration, delay, ease: "easeIn" }}
            style={{
              position: "absolute",
              left: `${left}vw`,
              top: 0,
              width: 8,
              height: 14,
              background: bg,
              borderRadius: 2,
            }}
          />
        );
      })}
    </div>
  );
}

/* ── 2. Typed-word spin overlay ────────────────────────────────── */
function TypedSpin({ show, onDone }) {
  return (
    <AnimatePresence onExitComplete={onDone}>
      {show && (
        <motion.div
          key="spin"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[85] grid place-items-center pointer-events-none"
          data-testid="typed-spin"
        >
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(circle at center, rgba(0,0,0,0.55), transparent 70%)" }}
          />
          <motion.div
            initial={{ scale: 0.4, rotate: 0, opacity: 0 }}
            animate={{ scale: [0.4, 1.1, 1], rotate: 720, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            style={{ filter: "drop-shadow(0 0 30px rgba(255,255,255,0.4))" }}
          >
            <Logo variant="full" className="h-40 md:h-56 w-auto text-primary-x" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ── 4. Keyboard shortcuts overlay ─────────────────────────────── */
function ShortcutsOverlay({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-md grid place-items-center px-4"
          data-testid="shortcuts-overlay"
          onClick={onClose}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 20, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-lg rounded-2xl border border-hairline-strong bg-surface-2 p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-muted-x font-mono-x">◇ Secret</div>
                <h3 className="mt-2 font-display text-2xl text-primary-x">Keyboard shortcuts</h3>
              </div>
              <kbd className="text-[10px] font-mono-x text-muted-x border border-hairline-strong rounded px-1.5 py-0.5">ESC</kbd>
            </div>
            <ul className="space-y-3 text-sm">
              {[
                ["?", "Open this menu"],
                ["g h", "Home"],
                ["g w", "Work"],
                ["g b", "Blog"],
                ["g c", "Contact"],
                ["⌘ K", "Search (on /components)"],
                ["↑↑↓↓←→←→ b a", "There be eggs here"],
              ].map(([k, l]) => (
                <li key={k} className="flex items-center justify-between gap-4 py-2 border-b border-hairline last:border-0">
                  <span className="text-secondary-x">{l}</span>
                  <kbd className="text-xs font-mono-x text-primary-x border border-hairline-strong rounded px-2 py-1">{k}</kbd>
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ── Main provider ──────────────────────────────────────────────── */
export function EasterEggsProvider({ children }) {
  const navigate = useNavigate();
  const [confetti, setConfetti] = useState(false);
  const [spin, setSpin] = useState(false);
  const [shortcuts, setShortcuts] = useState(false);

  const seq = useRef([]);
  const typed = useRef("");
  const gPress = useRef(0);
  const logoClicks = useRef({ count: 0, last: 0 });

  // Logo click handler (exposed to Navigation via context)
  const logoClick = useCallback(() => {
    const now = Date.now();
    if (now - logoClicks.current.last > 3000) {
      logoClicks.current.count = 0;
    }
    logoClicks.current.last = now;
    logoClicks.current.count += 1;
    if (logoClicks.current.count === 7) {
      logoClicks.current.count = 0;
      toast.success("🥳 You found us. Coffee's on us — hello@ulties.com");
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      // Ignore typing in inputs/textareas
      const tag = (e.target.tagName || "").toLowerCase();
      const editable = tag === "input" || tag === "textarea" || e.target.isContentEditable;
      const key = e.key;

      // ── Shortcuts overlay
      if (!editable && key === "?") {
        e.preventDefault();
        setShortcuts(true);
        return;
      }
      if (key === "Escape") setShortcuts(false);

      // ── Konami
      seq.current.push(key);
      if (seq.current.length > KONAMI.length) seq.current.shift();
      const matchKonami = seq.current.length === KONAMI.length &&
        seq.current.every((k, i) => k.toLowerCase() === KONAMI[i].toLowerCase());
      if (matchKonami) {
        seq.current = [];
        setConfetti(true);
        try { localStorage.setItem(UNLOCK_KEY, "1"); } catch (_) { /* ignore */ }
        toast.success("🎉 Secret unlocked — open /secret");
      }

      // ── Typed "ulties"
      if (!editable && /^[a-zA-Z]$/.test(key)) {
        typed.current = (typed.current + key.toLowerCase()).slice(-6);
        if (typed.current === "ulties") {
          typed.current = "";
          setSpin(true);
        }
      } else if (!editable && key !== "Shift") {
        // reset on non-letters
        typed.current = "";
      }

      // ── Vim-style g + ?
      if (!editable && key === "g") {
        gPress.current = Date.now();
        return;
      }
      if (!editable && Date.now() - gPress.current < 800) {
        gPress.current = 0;
        const targets = { h: "/", w: "/work", b: "/blog", c: "/contact" };
        if (targets[key]) {
          e.preventDefault();
          navigate(targets[key]);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <EasterEggsContext.Provider value={{ logoClick }}>
      <ConsoleBanner />
      {children}
      <KonamiConfetti show={confetti} onDone={() => setConfetti(false)} />
      <TypedSpin show={spin} onDone={() => setSpin(false)} />
      <ShortcutsOverlay open={shortcuts} onClose={() => setShortcuts(false)} />
    </EasterEggsContext.Provider>
  );
}

/* ── Secret page  (unlocked by Konami code) ───────────────────── */
export function SecretGreeting() {
  const unlocked =
    typeof window !== "undefined" && localStorage.getItem(UNLOCK_KEY) === "1";
  return (
    <div className="min-h-[60vh] grid place-items-center text-center px-6">
      {unlocked ? (
        <div className="max-w-xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">◇ You found it</div>
          <h1 className="mt-4 font-display text-5xl md:text-7xl font-bold tracking-[-0.04em] text-primary-x">
            Welcome to the basement.
          </h1>
          <p className="mt-6 text-secondary-x leading-relaxed">
            You typed the Konami code. We owe you a coffee — or a job, if you&apos;d rather. Reply to{" "}
            <a className="text-primary-x underline-offset-4 hover:underline" href="mailto:hello@ulties.com?subject=konami">
              hello@ulties.com
            </a>
            {" "}with the subject line <em>konami</em> and we&apos;ll send you a sticker pack.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/" className="text-primary-x text-sm">← Home</Link>
            <Link to="/careers" className="text-secondary-x hover:text-primary-x transition-colors text-sm">
              See open roles →
            </Link>
          </div>
        </div>
      ) : (
        <div className="max-w-md">
          <div className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">◇ Locked</div>
          <h1 className="mt-4 font-display text-4xl md:text-6xl font-bold tracking-[-0.04em] text-primary-x">
            Not yet.
          </h1>
          <p className="mt-6 text-secondary-x leading-relaxed">
            This room is hidden behind a small puzzle. Try{" "}
            <span className="text-primary-x">↑ ↑ ↓ ↓ ← → ← → b a</span>{" "}
            anywhere on the site, then come back.
          </p>
          <Link to="/" className="mt-10 inline-block text-secondary-x hover:text-primary-x transition-colors text-sm">
            ← Home
          </Link>
        </div>
      )}
    </div>
  );
}
